import { useState, useEffect, useCallback, useRef } from 'react';
import { CAROUSEL_CONFIG } from '../utils/constants';

export const useCarousel = (items = [], autoAdvance = true) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  // Function to start/restart the auto-advance interval
  const startInterval = useCallback(() => {
    if (!autoAdvance || items.length <= 1) return;

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Start new interval
    intervalRef.current = setInterval(() => {
      setIsTransitioning(true);
      setCurrentIndex((prevIndex) => {
        const nextIndex = prevIndex === items.length - 1 ? 0 : prevIndex + 1;
        return nextIndex;
      });
      
      // Reset transitioning state after animation completes
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setIsTransitioning(false);
      }, CAROUSEL_CONFIG.transitionDuration);
    }, CAROUSEL_CONFIG.autoAdvanceInterval);
  }, [autoAdvance, items.length]);

  const next = useCallback(() => {
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => 
      prevIndex === items.length - 1 ? 0 : prevIndex + 1
    );
    
    // Reset transitioning state after animation completes
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsTransitioning(false);
    }, CAROUSEL_CONFIG.transitionDuration);

    // Restart the interval after manual navigation
    startInterval();
  }, [items.length, startInterval]);

  const previous = useCallback(() => {
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? items.length - 1 : prevIndex - 1
    );
    
    // Reset transitioning state after animation completes
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsTransitioning(false);
    }, CAROUSEL_CONFIG.transitionDuration);

    // Restart the interval after manual navigation
    startInterval();
  }, [items.length, startInterval]);

  const goTo = useCallback((index) => {
    if (index >= 0 && index < items.length && index !== currentIndex) {
      setIsTransitioning(true);
      
      // Immediately update the current index - this ensures dots reflect the current screenshot
      setCurrentIndex(index);
      
      // Reset transitioning state after animation completes
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setIsTransitioning(false);
      }, CAROUSEL_CONFIG.transitionDuration);

      // Restart the interval after manual navigation - key for proper timer reset
      startInterval();
    }
  }, [items.length, currentIndex, startInterval]);

  // Initialize auto-advance on mount and when dependencies change
  useEffect(() => {
    startInterval();

    // Cleanup on unmount or dependency change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [startInterval]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    currentIndex, // This always represents the currently visible screenshot
    next,
    previous,
    goTo,
    isTransitioning,
    currentItem: items[currentIndex],
    totalItems: items.length,
  };
};